// /retention/risk — surface the Python Cox PH survival fit and the top
// active churn predictions. Sourced from dash_ml_survival_weights and
// dash_ml_churn_predictions (Unit 1 + Unit 2 in the AI/ML plan).
//
// Most of the time this page will show the survival card populated and
// a "classifier data-sparse" banner for the churn-predictions table —
// the 90d separation classifier needs ≥10 positives, and our current
// rolling 90d pool has 5.
//
// IDENTITY RESOLUTION (defect C3, 2026-08-16)
// -------------------------------------------
// The churn table used to render `p.member_name || p.member_id`. When the
// server's LEFT JOIN to dash_members matched nothing — which is the live
// state, because the classifier writes a 16-hex member hash into member_id
// where an airtable_id belongs — the fallback printed the raw hash under a
// heading calling those rows named active members, with 97%/92%/91% beside
// them. This page now renders an explicit unresolved state instead.
//
// Two independent guards, deliberately:
//  1. the API only returns rows that inner-joined to a real member record;
//  2. this page re-checks every row's name locally and withholds any row it
//     cannot name, even if the server said otherwise (stale bundle / cached
//     response / future regression).
// An honest "identity unresolved" always beats a confident wrong answer.

import { useEffect, useState } from "react";
import PageHeader from "../components/shared/PageHeader";
import Banner from "../components/shared/Banner";

interface Covariate {
  covariate: string;
  hazard_ratio: number;
  coef: number;
  p_value: number;
}

export interface ChurnRow {
  member_id: string;
  p_separation_90d: number;
  top_features: string | null;
  member_name: string;
  member_status: string;
}

/** Mirrors IdentityResolution in src/worker/ai/survival-weights-api.ts. */
export interface IdentityResolution {
  status: "no_predictions" | "unresolved" | "partial" | "resolved";
  active_predictions: number;
  resolved: number;
  unresolved: number;
}

interface Response {
  model_version: string | null;
  fitted_at: string | null;
  covariates: Covariate[];
  churn_predictions: ChurnRow[];
  /** Absent only if an older worker build answers a newer bundle. */
  identity_resolution?: IdentityResolution;
  note: string | null;
}

export const IDENTITY_UNRESOLVED_TITLE =
  "Identity unresolved — predictions cannot be attributed";

/** A row is only a person if it carries a real name. A hash is not a name. */
function isAttributable(row: ChurnRow): boolean {
  return (row.member_name ?? "").trim() !== "";
}

export interface ChurnSectionState {
  status: IdentityResolution["status"];
  /** Rows safe to render as named people. Never contains an unnamed id. */
  rows: ChurnRow[];
  /** Size of the classifier's active cohort. */
  activePredictions: number;
  /** Cohort rows that could not be attributed and are withheld. */
  withheld: number;
}

/**
 * Decide what the churn section may show.
 *
 * Rows are filtered locally first — an unnamed row can never render, whatever
 * the server claims. The server's cohort counts are then reconciled in, since
 * only the server sees the members trimmed off by the LIMIT 20; but if the
 * server handed us any row we cannot name, its `resolved` count is treated as
 * untrustworthy and we fall back to what we can prove.
 */
export function churnSectionState(
  predictions: ChurnRow[],
  identity?: IdentityResolution,
): ChurnSectionState {
  const received = predictions ?? [];
  const rows = received.filter(isAttributable);
  const serverSentUnnamed = rows.length < received.length;

  const resolvedTotal = serverSentUnnamed
    ? rows.length
    : Math.max(identity?.resolved ?? rows.length, rows.length);
  const activePredictions = Math.max(
    identity?.active_predictions ?? 0,
    received.length,
    resolvedTotal,
  );
  const withheld = Math.max(0, activePredictions - resolvedTotal);

  const status: IdentityResolution["status"] =
    activePredictions === 0
      ? "no_predictions"
      : rows.length === 0
        ? "unresolved"
        : withheld > 0
          ? "partial"
          : "resolved";
  return { status, rows, activePredictions, withheld };
}

function formatP(p: number): string {
  if (p < 1e-10) return "<1e-10";
  if (p < 0.001) return p.toExponential(1);
  return p.toFixed(4);
}

function hrDirection(hr: number): { label: string; color: string } {
  if (hr > 1.05)
    return { label: `↑ risk ×${hr.toFixed(2)}`, color: "text-red-600" };
  if (hr < 0.95)
    return { label: `↓ risk ×${hr.toFixed(2)}`, color: "text-emerald-600" };
  return { label: `neutral ×${hr.toFixed(2)}`, color: "text-theme-muted" };
}

/**
 * The churn-prediction block. Renders named members only; anything it cannot
 * attribute is withheld and reported as unresolved, never printed as an id.
 */
export function ChurnPredictionSection({
  predictions,
  identity,
}: {
  predictions: ChurnRow[];
  identity?: IdentityResolution;
}) {
  const state = churnSectionState(predictions, identity);
  const showTable = state.rows.length > 0;

  return (
    <section className="glass rounded-2xl p-5 md:p-6">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="text-sm font-semibold text-theme-primary uppercase tracking-wider">
          {showTable ? "Highest-risk members (90d)" : "Churn predictions (90d)"}
        </h2>
        <div className="text-xs text-theme-muted">
          {state.status === "resolved" &&
            `Top ${state.rows.length} of the classifier's active cohort`}
          {state.status === "partial" &&
            `Top ${state.rows.length} shown · ${state.withheld} of ${state.activePredictions} withheld (identity unresolved)`}
          {state.status === "unresolved" &&
            `${state.activePredictions} prediction${
              state.activePredictions === 1 ? "" : "s"
            } · 0 attributable`}
          {state.status === "no_predictions" &&
            "Classifier data-sparse — no predictions yet"}
        </div>
      </div>

      {state.status === "unresolved" && (
        <Banner variant="error" title={IDENTITY_UNRESOLVED_TITLE}>
          <p className="leading-relaxed">
            The classifier has written {state.activePredictions} active-cohort
            prediction{state.activePredictions === 1 ? "" : "s"}, but none of
            them resolve to a member record:{" "}
            <code className="font-mono">
              dash_ml_churn_predictions.member_id
            </code>{" "}
            holds a member hash where an Airtable record id is expected, so the
            join to <code className="font-mono">dash_members</code> returns
            nothing. Names, statuses and separation probabilities are{" "}
            <strong className="font-semibold">withheld</strong> rather than
            shown against ids nobody can identify.
          </p>
          <p className="mt-2 leading-relaxed">
            This is an upstream defect in the classifier writer, not a finding
            about any member, and nothing here should be read as a risk list.
            The section repopulates automatically once the writer persists the
            member&apos;s record id.
          </p>
        </Banner>
      )}

      {state.status === "partial" && (
        <Banner variant="warn" className="mb-4">
          {state.withheld} of {state.activePredictions} active-cohort
          predictions could not be attributed to a member record and are
          withheld. The list below is therefore incomplete — do not read it as
          the full risk picture.
        </Banner>
      )}

      {state.status === "no_predictions" && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3">
          <p className="text-sm font-semibold text-amber-700 dark:text-amber-300 mb-1">
            Finding: classifier offline (data-sparse)
          </p>
          <p className="text-xs text-amber-800/90 dark:text-amber-100/90 leading-relaxed">
            The 90-day separation classifier needs ≥10 positive events in the
            rolling window to produce trustworthy predictions. With the current
            active cohort that bar is not yet met, so the model is intentionally
            not running. This is itself a governance signal:
            <strong className="font-semibold">
              {" "}
              a small department is statistically hard to predict
            </strong>
            , which means the board cannot rely on a churn model alone —
            qualitative root-cause work (see /retention/root-cause) and
            intervention tracking (see /forge/interventions) carry more weight
            here. The page will populate automatically once the
            classifier&apos;s gate clears.
          </p>
        </div>
      )}

      {showTable && (
        <table className="w-full text-sm">
          <caption className="sr-only">
            Attributed churn predictions per member: status, 90-day separation
            probability, and top contributing features
          </caption>
          <thead>
            <tr className="text-left text-xs text-theme-muted border-b border-theme-subtle">
              <th scope="col" className="py-2 pr-3">
                Member
              </th>
              <th scope="col" className="py-2 px-3">
                Status
              </th>
              <th scope="col" className="py-2 px-3 text-right">
                p(separate 90d)
              </th>
              <th scope="col" className="py-2 pl-3">
                Top features
              </th>
            </tr>
          </thead>
          <tbody>
            {state.rows.map((p) => (
              <tr
                key={p.member_id}
                className="hover:bg-theme-overlay/50 transition-colors"
              >
                <td className="py-2 pr-3 text-theme-primary">
                  {p.member_name}
                </td>
                <td className="py-2 px-3 text-xs text-theme-muted">
                  {p.member_status || "—"}
                </td>
                <td className="py-2 px-3 text-right font-mono tabular-nums text-theme-primary font-semibold">
                  {(p.p_separation_90d * 100).toFixed(1)}%
                </td>
                <td className="py-2 pl-3 text-xs text-theme-muted font-mono truncate max-w-md">
                  {p.top_features ?? "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

export default function RetentionRisk() {
  const [data, setData] = useState<Response | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/ai/survival-weights", { credentials: "same-origin" })
      .then((r) =>
        r.ok
          ? (r.json() as Promise<Response>)
          : Promise.reject(new Error(`HTTP ${r.status}`)),
      )
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <div className="glass rounded-xl p-6 text-sm text-red-600 mx-4 md:mx-8">
        Failed to load: {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="glass rounded-xl p-6 text-sm text-theme-muted mx-4 md:mx-8">
        Loading retention risk…
      </div>
    );
  }

  const sorted = [...data.covariates].sort((a, b) => a.p_value - b.p_value);

  return (
    <div className="space-y-6 px-4 md:px-8 py-6 max-w-6xl mx-auto">
      <PageHeader
        eyebrow="RETENTION · RISK"
        title="Member retention risk"
        subtitle="Cox PH survival fit on the member cohort and the active XGBoost churn predictions. Drives the multipliers in nightly-intelligence risk scoring."
      />

      {data.note && <Banner variant="warn">{data.note}</Banner>}

      <section className="glass rounded-2xl p-5 md:p-6">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="text-sm font-semibold text-theme-primary uppercase tracking-wider">
            Cox PH survival fit
          </h2>
          <div className="text-xs text-theme-muted tabular-nums">
            {data.model_version ?? "—"}
            {data.fitted_at && (
              <span className="ml-3">
                fitted {data.fitted_at.slice(0, 19)} UTC
              </span>
            )}
          </div>
        </div>
        {sorted.length === 0 ? (
          <p className="text-sm text-theme-muted">
            No covariates fit yet. Run{" "}
            <code className="font-mono">
              uv run python -m churn.survival --write
            </code>
            .
          </p>
        ) : (
          <table className="w-full text-sm">
            <caption className="sr-only">
              Cox proportional-hazards covariates with hazard ratio,
              coefficient, p-value, and effect direction
            </caption>
            <thead>
              <tr className="text-left text-xs text-theme-muted border-b border-theme-subtle">
                <th scope="col" className="py-2 pr-3">
                  Covariate
                </th>
                <th scope="col" className="py-2 px-3 text-right">
                  Hazard ratio
                </th>
                <th scope="col" className="py-2 px-3 text-right">
                  coef
                </th>
                <th scope="col" className="py-2 px-3 text-right">
                  p-value
                </th>
                <th scope="col" className="py-2 pl-3">
                  Effect
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((c) => {
                const dir = hrDirection(c.hazard_ratio);
                return (
                  <tr
                    key={c.covariate}
                    className="hover:bg-theme-overlay/50 transition-colors"
                  >
                    <td className="py-2 pr-3 font-mono text-theme-primary">
                      {c.covariate}
                    </td>
                    <td className="py-2 px-3 text-right font-mono tabular-nums text-theme-primary">
                      {c.hazard_ratio.toFixed(4)}
                    </td>
                    <td className="py-2 px-3 text-right font-mono tabular-nums text-theme-muted">
                      {c.coef.toFixed(4)}
                    </td>
                    <td className="py-2 px-3 text-right font-mono tabular-nums text-theme-muted">
                      {formatP(c.p_value)}
                    </td>
                    <td
                      className={`py-2 pl-3 text-xs font-semibold ${dir.color}`}
                    >
                      {dir.label}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <ChurnPredictionSection
        predictions={data.churn_predictions}
        identity={data.identity_resolution}
      />

      <footer className="text-xs text-theme-muted">
        <p>
          Survival: <code className="font-mono">dash_ml_survival_weights</code>{" "}
          (lifelines Cox PH). Classifier:{" "}
          <code className="font-mono">dash_ml_churn_predictions</code> (XGBoost
          90d). Both refit by the{" "}
          <code className="font-mono">data-sync-ml-weekly</code> systemd timer.
        </p>
      </footer>
    </div>
  );
}
